import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';

import { useLazyQuery, useMutation } from '@apollo/client';
import { getUserToken } from '@utils/userUtils';
import { isEmpty } from '@utils/validationUtils';

import { IAppVisibilityDetails } from './app-settings/types';
import { IUserDetails } from './user/types';
import { getAppVisibilitySettings, userDetailsQuery } from './queries';
import type { AppDispatch, RootState } from './store';

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

type UseUserDetailsServiceApi = {
  getUserDetails: () => Promise<IUserDetails | null>;
};
export const useUserDetailsApi = (): UseUserDetailsServiceApi => {
  const [getUserDetails] = useLazyQuery<{
    core: {
      getCurrentUser: {
        access: string;
        accessFailedCount: number;
        code: string;
        concurrencyStamp: string;
        allowPolicyCorrection: boolean;
        allowPolicyCorrectionAfterClosing: boolean;
        allowPolicyEndorsement: boolean;
        allowPolicyCancellation: boolean;
        amendmentIssueDateEditRights?: boolean;
        policyIssueDateEditRights?: boolean;
        allowTransactionModification?: boolean;
        planCoverEditRights?: boolean;
        reinsuranceClosingRights?: boolean;
        productionClosingRights?: boolean;
        email: string;
        emailConfirmed: boolean;
        firstName: string;
        id: string;
        lastName: string;
        lockoutEnabled: boolean;
        lockoutEnd: string;
        middleName: string;
        normalizedEmail: string;
        normalizedUserName: string;
        passwordHash: string;
        phoneNumber: string;
        phoneNumberConfirmed: boolean;
        securityStamp: string;
        status: string;
        twoFactorEnabled: boolean;
        type: string;
        userName: string;
        userRoles: {
          role: {
            name: string;
          };
        };
        editableChargesLines: { lineID: string }[];
      };
    };
  }>(userDetailsQuery, { fetchPolicy: 'network-only' });

  return {
    getUserDetails: () => {
      if (isEmpty(getUserToken())) {
        return null;
      }

      return getUserDetails().then((res) => {
        const { data, error } = res;

        if (error) {
          return null;
        }

        if (data) {
          const user = data.core.getCurrentUser;
          const roleNames = Array.isArray(user.userRoles)
            ? user.userRoles.map((ur) => ur.role.name)
            : [];
          const userDetails: IUserDetails = {
            haveWeakPassword: false,
            info: {
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              access: user.access,
              email: user.email,
              agencyId: [],
              agentId: '',
              businessUser_AllowGroupPolicy: false,
              businessUser_AllowPolicyCorrection: user.allowPolicyCorrection,
              businessUser_AllowPolicyCorrectionAfterClosing:
                user.allowPolicyCorrectionAfterClosing ?? false,
              businessUser_AllowEndorsementCostEdits: false,
              businessUser_AllowEndorsement: user.allowPolicyEndorsement,
              businessUser_AllowPolicyCancellation:
                user.allowPolicyCancellation,
              businessUser_AllowClauseEdits: false,
              businessUser_AllowPlanCoverEdits:
                user.planCoverEditRights ?? false,
              businessUser_AllowTransactionModification:
                user.allowTransactionModification ?? false,
              businessUser_AmendmentIssueDateEditRights:
                user.amendmentIssueDateEditRights ?? false,
              businessUser_ReinsuranceClosingRights:
                user.reinsuranceClosingRights ?? false,
              businessUser_ProductionClosingRights:
                user.productionClosingRights ?? false,
              businessUser_PolicyIssueDateEditRights:user.policyIssueDateEditRights ?? false,
              businessUser_Access: {
                Id: '',
                Code: '',
                Title: ''
              },
              editableChargesLines: user.editableChargesLines.map((item) => ({
                lineID: item.lineID
              })),
              products: [],
              eligiblePlans: [],
              agentType: ''
            },
            userRoles: roleNames as IUserDetails['userRoles'],
            updatedUserRoles: roleNames as IUserDetails['updatedUserRoles'],
            linesOfBusinessStatuses: {
              Active: false,
              Code: '',
              Title: ''
            }
          };

          return userDetails;
        }

        return null;
      });
    }
  };
};

type UseAppDetailsServiceApi = {
  getAppDetails: () => Promise<IAppVisibilityDetails | null>;
};
export const useAppDetailsApi = (): UseAppDetailsServiceApi => {
  const [getAppDetails] = useMutation<{
    insurance: {
      actions: {
        getAppVisibilitySettings: {
          IsPlanConfigurationEnabled?: boolean;
          IsSalesForceAppEnabled?: boolean;
        };
      };
    };
  }>(getAppVisibilitySettings);

  return {
    getAppDetails: () => {
      return new Promise((resolve) => {
        resolve({
          isPlanConfigurationEnabled: true,
          isSalesForceAppEnabled: true
        });
      });
    }
  };
};
